import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Check, X, Sparkles, Building2, Rocket, Store, Star,
  ArrowRight, Menu, X as Close, ShieldCheck, Phone, Tag,
} from 'lucide-react';
import appConfig from '../../config/appConfig';
import SEO from '../../components/SEO';
import { platformAPI } from '../../services/api';
import { PLANS as PLAN_CONFIG, PLAN_ORDER, fmtLimit, AUDIENCES, vehicleNoun, limitChips, planFeatures } from '../../config/plans';

// ── Plan catalogue ────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH: config/plans.js. To change prices, limits or
// features, edit that one file — this page (and the Subscription page) follow.
// Only display extras (icon, CTA text) live here.
const META = {
  Free: { icon: Sparkles, cta: 'Start free', to: '/register', usersLabel: 'user (owner)' },
  Basic: { icon: Store, cta: 'Choose Basic', to: '/register', usersLabel: 'team users' },
  Professional: { icon: Rocket, cta: 'Choose Professional', to: '/register', usersLabel: 'team users' },
  Enterprise: { icon: Building2, cta: 'Contact sales', to: null, usersLabel: 'users' },
};

// Prices and caps are identical for both audiences — only the wording and the
// rows worth showing differ. A Buy/Sell agency has no Drivers page at all.
const buildPlans = (audience) => PLAN_ORDER.map((id) => {
  const p = PLAN_CONFIG[id];
  const m = META[id] || {};
  return {
    ...p,
    icon: m.icon || Sparkles,
    cta: m.cta || (p.custom ? 'Contact sales' : p.monthly === 0 ? 'Start free' : `Choose ${p.name}`),
    to: m.to === undefined ? '/register' : m.to,
    highlight: !!p.popular,
    badge: p.popular ? 'Most Popular' : null,
    features: planFeatures(p, audience),
    limitChips: limitChips(p, audience),
  };
});

// ── Feature comparison matrix ─────────────────────────────────────────────
// Numeric rows are derived from the config; boolean rows are display-only.
const numRow = (key, label) => ({ label, values: PLAN_ORDER.map((id) => String(fmtLimit(PLAN_CONFIG[id].limits[key]))) });

// Fleet management (bookings, journeys, drivers, maintenance, inspections) is free
// for everyone. Paid tiers sell scale, growth and branding.
const RENTAL_COMPARISON = [
  numRow('vehicles', 'Vehicles'),
  numRow('users', 'Team users'),
  numRow('drivers', 'Drivers'),
  { label: 'Bookings & journeys', values: [true, true, true, true] },
  { label: 'Drivers & assignments', values: [true, true, true, true] },
  { label: 'Maintenance & document tracking', values: [true, true, true, true] },
  { label: 'Buy / Sell listings', values: [true, true, true, true] },
  { label: 'Public share pages', values: [true, true, true, true] },
  { label: 'Bulk import (CSV / Excel)', values: [false, true, true, true] },
  { label: 'Trip packages', values: [false, true, true, true] },
  { label: 'WhatsApp share + OTP booking', values: [false, true, true, true] },
  { label: 'One-way trips', values: [false, false, true, true] },
  { label: 'Own logo on share pages', values: [false, false, true, true] },
  { label: 'Advanced reports & exports', values: [false, false, true, true] },
  { label: 'Dedicated onboarding', values: [false, false, false, true] },
  { label: 'Support', values: ['Community', 'Email', 'Priority', 'Dedicated + SLA'] },
];

// No drivers, journeys, trips or inspections — none of it exists for a dealer.
// Listings are available on Free, otherwise the Free tier would be useless here.
const SALE_COMPARISON = [
  numRow('vehicles', 'Listings'),
  numRow('users', 'Team users'),
  { label: 'Buy / Sell listings', values: [true, true, true, true] },
  { label: 'Public share pages', values: [true, true, true, true] },
  { label: 'Buyer enquiries with OTP', values: [true, true, true, true] },
  { label: 'Document expiry tracking', values: [true, true, true, true] },
  { label: 'Bulk listing import (CSV / Excel)', values: [false, true, true, true] },
  { label: 'WhatsApp share + OTP enquiries', values: [false, true, true, true] },
  { label: 'Own logo on share pages', values: [false, false, true, true] },
  { label: 'Advanced reports & exports', values: [false, false, true, true] },
  { label: 'Dedicated onboarding', values: [false, false, false, true] },
  { label: 'Support', values: ['Community', 'Email', 'Priority', 'Dedicated + SLA'] },
];

// The rental matrix is the full one — it already covers Buy/Sell listings and
// bulk import, so a 'both' owner sees every row.
const comparisonFor = (audience) => (audience === 'sale' ? SALE_COMPARISON : RENTAL_COMPARISON);

// Only one add-on is real. WhatsApp usage billing and SMS credit packs have no
// metering or credit model behind them, so we don't sell them.
// This isn't self-serve — an admin sets the vehicle count by hand after payment.
const addonsFor = (audience) => [
  {
    title: `Extra ${vehicleNoun(audience)}`,
    price: '₹49',
    unit: `/${audience === 'sale' ? 'listing' : 'vehicle'}/mo`,
    desc: `Need more than your plan allows? Talk to us and we'll add them to your account — no need to jump a whole tier.`,
  },
];

const FAQ = [
  { q: 'Can I start free and upgrade later?', a: 'Yes. Begin on the Free plan and upgrade anytime as your fleet grows — your data carries over instantly.' },
  { q: 'How is billing charged?', a: 'Monthly or annually via UPI, cards or net-banking. Annual billing gives you roughly two months free.' },
  { q: 'What happens if I exceed my vehicle limit?', a: 'You’ll be prompted to upgrade or add vehicles as an add-on. Existing data is never deleted.' },
  { q: 'Do customers pay anything?', a: 'No. Plans are for travel-agency owners. Customers browse and book your vehicles for free.' },
  { q: 'Can I cancel anytime?', a: 'Yes. You keep access until the end of your billing period, then drop back to Free.' },
];

const inr = (n) => new Intl.NumberFormat('en-IN').format(n);

function Cell({ v }) {
  if (v === true) return <Check className="w-5 h-5 text-emerald-500 mx-auto" />;
  if (v === false) return <X className="w-4 h-4 text-gray-300 mx-auto" />;
  return <span className="text-sm text-gray-700 font-medium">{v}</span>;
}

export default function Pricing() {
  const [annual, setAnnual] = useState(true);
  // Visitors aren't logged in, so they tell us which side of the product they're here for.
  const [audience, setAudience] = useState('rental');
  const PLANS = buildPlans(audience);
  const COMPARISON = comparisonFor(audience);
  const ADDONS = addonsFor(audience);
  const [menuOpen, setMenuOpen] = useState(false);
  const [discount, setDiscount] = useState({ enabled: false, percent: 0, label: '' });
  const salesHref = `https://wa.me/${(appConfig.contact?.phone || '').replace(/\D/g, '')}?text=${encodeURIComponent('Hi, I’m interested in the Enterprise plan for Wheels4Rent.')}`;
  // Logged-in owners go to their in-app Subscription page (to request an upgrade);
  // visitors go to Register to sign up first.
  const loggedIn = !!localStorage.getItem('token');
  const planTo = (p) => (loggedIn ? '/subscription' : p.to);

  // Read the admin-configured discount from platform settings.
  useEffect(() => {
    platformAPI.getSettings().then((res) => {
      const fd = res.data?.settings?.founderDiscount;
      if (fd?.enabled && fd.percent > 0) setDiscount({ enabled: true, percent: fd.percent, label: fd.label || 'Launch offer' });
    }).catch(() => {});
  }, []);

  const withDiscount = (amount) => (discount.enabled && amount > 0
    ? Math.round(amount * (1 - discount.percent / 100)) : amount);

  const priceLabel = (p) => {
    if (p.custom) return { big: 'Custom', small: 'Talk to us' };
    if (p.monthly === 0) return { big: '₹0', small: 'forever' };
    const base = annual ? p.annual : p.monthly;
    const disc = withDiscount(base);
    const small = annual ? `/yr · ≈ ₹${inr(Math.round(disc / 12))}/mo` : '/mo';
    return { big: `₹${inr(disc)}`, small, orig: disc !== base ? `₹${inr(base)}` : null };
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Pricing" description="Simple plans for travel agencies — rent, sell & manage your fleet on Wheels4Rent." path="/pricing" />

      {/* Nav */}
      <nav className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/home" className="flex items-center gap-2 hover:opacity-90">
              <img src={appConfig.logo} alt={appConfig.name} className="w-9 h-9 object-contain rounded-lg" />
              <span className="text-xl font-bold">{appConfig.name}</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/browse" className="text-gray-300 hover:text-white text-sm font-medium">Browse Vehicles</Link>
              <Link to="/pricing" className="text-white text-sm font-semibold">Pricing</Link>
              <Link to="/about" className="text-gray-300 hover:text-white text-sm font-medium">About Us</Link>
              <Link to="/support" className="text-gray-300 hover:text-white text-sm font-medium">Support</Link>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <Link to="/login" className="px-4 py-2 text-sm font-medium border border-white/30 rounded-lg hover:bg-white/10">Login</Link>
              <Link to="/register" className="px-4 py-2 text-sm font-medium bg-blue-600 rounded-lg hover:bg-blue-700">Register</Link>
            </div>
            <button className="md:hidden p-2" onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <Close className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-white/10 px-4 py-4 space-y-3">
            <Link to="/browse" className="block text-gray-300 py-1">Browse Vehicles</Link>
            <Link to="/pricing" className="block text-white font-semibold py-1">Pricing</Link>
            <Link to="/about" className="block text-gray-300 py-1">About Us</Link>
            <div className="flex gap-3 pt-3 border-t border-white/10">
              <Link to="/login" className="flex-1 text-center px-4 py-2 text-sm border border-white/30 rounded-lg">Login</Link>
              <Link to="/register" className="flex-1 text-center px-4 py-2 text-sm bg-blue-600 rounded-lg">Register</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-b from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 py-16 sm:py-20 text-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold text-blue-200 mb-5">
            <ShieldCheck className="w-3.5 h-3.5" /> Built for travel agencies
          </span>
          <h1 className="text-3xl sm:text-5xl font-extrabold leading-tight">Pricing that grows with your fleet</h1>
          <p className="mt-4 text-gray-300 text-lg max-w-2xl mx-auto">
            Rent, sell and manage your vehicles from one place. Start free — upgrade when you’re ready.
          </p>

          {discount.enabled && (
            <div className="mt-5 inline-flex items-center gap-2 bg-emerald-400/15 text-emerald-200 border border-emerald-300/30 rounded-full px-4 py-1.5 text-sm font-semibold">
              <Tag className="w-4 h-4" /> {discount.label} — {discount.percent}% off all paid plans
            </div>
          )}

          {/* Audience toggle — the plans cost the same, but a dealer and a fleet
              owner care about completely different things. */}
          <div className="mt-8 flex justify-center">
            <div className="inline-flex items-center gap-1 bg-white/10 rounded-full p-1">
              {AUDIENCES.map((a) => (
                <button
                  key={a.value}
                  onClick={() => setAudience(a.value)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition ${audience === a.value ? 'bg-white text-gray-900' : 'text-white'}`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          {/* Billing toggle */}
          <div className="mt-4 inline-flex items-center gap-3 bg-white/10 rounded-full p-1">
            <button onClick={() => setAnnual(false)} className={`px-5 py-2 rounded-full text-sm font-semibold transition ${!annual ? 'bg-white text-gray-900' : 'text-white'}`}>Monthly</button>
            <button onClick={() => setAnnual(true)} className={`px-5 py-2 rounded-full text-sm font-semibold transition flex items-center gap-2 ${annual ? 'bg-white text-gray-900' : 'text-white'}`}>
              Annual <span className="text-[11px] font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-full">2 months free</span>
            </button>
          </div>
        </div>
      </section>

      {/* Plan cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 pb-4">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((p) => {
            const Icon = p.icon;
            const price = priceLabel(p);
            return (
              <div key={p.id}
                className={`relative rounded-3xl bg-white flex flex-col ${p.highlight ? 'ring-2 ring-blue-600 shadow-2xl lg:-translate-y-2' : 'border border-gray-200 shadow-sm'}`}>
                {p.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-bold shadow">
                    <Star className="w-3 h-3 fill-current" /> {p.badge}
                  </span>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2">
                    <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${p.highlight ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600'}`}><Icon className="w-5 h-5" /></span>
                    <div>
                      <h3 className="font-bold text-gray-900">{p.name}</h3>
                      <p className="text-xs text-gray-500">{p.tagline}</p>
                    </div>
                  </div>

                  <div className="mt-5">
                    <div className="flex items-end gap-2">
                      <div className="text-3xl font-extrabold text-gray-900">{price.big}</div>
                      {price.orig && <span className="text-base text-gray-400 line-through mb-1">{price.orig}</span>}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5">{price.small}</div>
                    {price.orig && (
                      <span className="inline-block mt-2 text-[11px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">{discount.percent}% OFF</span>
                    )}
                  </div>

                  {p.custom ? (
                    <a href={salesHref} target="_blank" rel="noopener noreferrer"
                      className="mt-5 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold bg-gray-900 text-white hover:bg-gray-800">
                      <Phone className="w-4 h-4" /> {p.cta}
                    </a>
                  ) : (
                    <Link to={planTo(p)}
                      className={`mt-5 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold ${p.highlight ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}>
                      {loggedIn && p.monthly > 0 ? `Get ${p.name}` : p.cta} <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>

                <div className="px-6 pb-6 flex-1">
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {p.limitChips.map((l) => (
                      <span key={l} className="text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-2.5 py-1">{l}</span>
                    ))}
                  </div>
                  <ul className="space-y-2.5">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                        <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-gray-400 mt-6">Prices in INR, exclusive of GST. Cancel anytime.</p>
      </section>

      {/* Comparison */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 text-center mb-8">Compare all features</h2>
        <div className="overflow-x-auto rounded-2xl border border-gray-200">
          <table className="w-full min-w-[640px] text-left">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="p-4 text-sm font-semibold text-gray-500">Feature</th>
                {PLANS.map((p) => (
                  <th key={p.id} className={`p-4 text-center text-sm font-bold ${p.highlight ? 'text-blue-600' : 'text-gray-900'}`}>{p.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row, i) => (
                <tr key={row.label} className={i % 2 ? 'bg-gray-50/50' : 'bg-white'}>
                  <td className="p-4 text-sm text-gray-700">{row.label}</td>
                  {row.values.map((v, j) => (
                    <td key={j} className="p-4 text-center"><Cell v={v} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Add-ons */}
      <section className="bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 text-center mb-3">Add-ons</h2>
          <p className="text-center text-gray-500 mb-8">Add capacity to any paid plan — we set it up for you.</p>
          <div className={`grid gap-5 ${ADDONS.length > 1 ? 'sm:grid-cols-3' : 'max-w-md mx-auto'}`}>
            {ADDONS.map((a) => (
              <div key={a.title} className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900">{a.title}</h3>
                <div className="mt-2 text-2xl font-extrabold text-blue-600">{a.price}<span className="text-sm font-medium text-gray-400"> {a.unit}</span></div>
                <p className="text-sm text-gray-500 mt-2">{a.desc}</p>
                <a
                  href={salesHref}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-700"
                >
                  <Phone className="w-4 h-4" /> Talk to us
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 py-16">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 text-center mb-8">Frequently asked</h2>
        <div className="space-y-3">
          {FAQ.map((f) => (
            <details key={f.q} className="group rounded-2xl border border-gray-200 bg-white p-5 [&_svg]:open:rotate-45">
              <summary className="flex items-center justify-between cursor-pointer list-none font-semibold text-gray-900">
                {f.q}
                <span className="text-blue-600 transition-transform text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm text-gray-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-14 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold">Ready to grow your travel business?</h2>
          <p className="mt-3 text-blue-100">Set up your agency in minutes — no card required to start.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/register" className="px-6 py-3 rounded-xl font-bold bg-white text-blue-700 hover:bg-blue-50">Start free</Link>
            <a href={salesHref} target="_blank" rel="noopener noreferrer" className="px-6 py-3 rounded-xl font-bold border border-white/40 hover:bg-white/10">Talk to sales</a>
          </div>
        </div>
      </section>

      <footer className="bg-gray-900 text-gray-400 text-center text-sm py-8">
        © {appConfig.name} · {appConfig.tagline}
      </footer>
    </div>
  );
}
